/**
 * @file 新版导航体系 · 导航模型（/next）
 *
 * 导航与页面权限的唯一事实来源（旧版 /app 界面已下线）。
 * 当前角色来自登录态的激活角色（/auth/switch-role 真实切换）。
 *
 * 菜单规则（2026-10-16 勾选制；2026-10-20 方案 C 生切为顶栏下拉）：
 * 1. 顶栏一级 = 模块（+ 工作台）：稳定、按权限显示，不随个人定制；
 * 2. 二级进下拉：可见 1 页 → 点一级直达；≥2 页 → 下拉列出；基础配置进「更多」；
 * 3. 工作台 = 落地页：复用可编辑画布（自定义配置，壳层不管工作台内容）；
 * 4. 权限勾选 = 页面可见的唯一开关（叠加租户订阅裁剪）：
 *    模块需持有模块级菜单码（menu:*）且至少 1 页已授权；
 *    无角色档位概念，自定义角色与内置角色同一套规则。
 *
 * 页面权限码与角色管理里勾选的菜单码一致。
 */
import { computed } from 'vue'
import { useUserStore } from '@/stores/user'
import type { ModuleCode } from '@/configs/tenant-modules'

export interface NextPageDef {
  id: string
  title: string
  path: string
  /** 页面专属图标（顶栏下拉方盒线标） */
  icon: string
  /** 下拉副文案（方案 C 二级面板一行说明） */
  blurb?: string
  /** 所属订阅模块码：租户未订阅该模块时此页面隐藏（与运营端"已订阅模块"颗粒度一致） */
  module: ModuleCode
  /** 页面权限码：登录态权限非空且不含该码时隐藏 */
  permission: string
}

export interface NextModuleDef {
  id: string
  title: string
  icon: string
  /** 模块级菜单权限码（对齐旧版菜单分组码）：未持有时整个模块隐藏 */
  permission: string
  /** 模块下的二级页面（数组顺序即左栏顺序） */
  pages: NextPageDef[]
  /** 点击一级模块后的默认落地页（页面 id；不可见时回退首个可见页） */
  landing: string
}

export const WORKBENCH_PATH = '/next/workbench'

/** 跨模块功能入口（菜单卡底图标直达，不属于任何模块） */
export const NEXT_FUNCTION_ENTRIES: { title: string; path: string; icon: string }[] = [
  { title: 'AI 助理', path: '/next/ai-assistant', icon: 'bot' },
  { title: '帮助中心', path: '/next/help', icon: 'help-circle' }
]

/** 不归属模块、所有角色均可访问的路径 */
const ALWAYS_ALLOWED_PATHS = [WORKBENCH_PATH, ...NEXT_FUNCTION_ENTRIES.map((e) => e.path)]

export const NEXT_MODULES: NextModuleDef[] = [
  {
    id: 'academic',
    title: '教务管理',
    icon: 'graduation-cap',
    permission: 'menu:academic',
    landing: 'selection',
    pages: [
      { id: 'selection', title: '选科管理', path: '/next/edu/selection', icon: 'clipboard-list', blurb: '志愿填报与录取', module: 'subject-selection', permission: 'academic:subject-selection' },
      { id: 'school-based', title: '校本选课', path: '/next/edu/school-based', icon: 'book', blurb: '校本课程报名', module: 'course-selection', permission: 'academic:course-selection' },
      { id: 'enrollment', title: '入学分班', path: '/next/edu/enrollment', icon: 'user-plus', blurb: '新生分班编排', module: 'enrollment', permission: 'academic:enrollment' },
      { id: 'subject-class', title: '选科分班', path: '/next/edu/subject-class', icon: 'shuffle', blurb: '走班分班结果', module: 'subject-class', permission: 'academic:subject-class' }
    ]
  },
  {
    id: 'schedule',
    title: '课表管理',
    icon: 'calendar',
    permission: 'menu:timetable',
    landing: 'tasks',
    pages: [
      { id: 'tasks', title: '排课任务', path: '/next/schedule/tasks', icon: 'calendar-plus', blurb: '任务创建与发布', module: 'timetable', permission: 'academic:schedule' },
      { id: 'query', title: '课表查询', path: '/next/schedule/query', icon: 'search', blurb: '全校课表检索', module: 'timetable', permission: 'academic:timetable-query' },
      { id: 'maintenance', title: '课表维护', path: '/next/schedule/maintenance', icon: 'sliders', blurb: '调课与冲突处理', module: 'timetable', permission: 'academic:schedule-maintenance' },
      { id: 'my', title: '我的课表', path: '/next/schedule/my', icon: 'calendar', blurb: '个人授课课表', module: 'timetable', permission: 'teacher:my-timetable' }
    ]
  },
  {
    id: 'exam-eval',
    title: '考务与评价',
    icon: 'clipboard-check',
    permission: 'menu:exam-eval',
    landing: 'exams',
    pages: [
      { id: 'exams', title: '考试管理', path: '/next/edu/exams', icon: 'clipboard-list', blurb: '考试活动与场次', module: 'exam-activities', permission: 'exam:activities' },
      { id: 'scores', title: '成绩管理', path: '/next/edu/scores', icon: 'bar-chart', blurb: '成绩录入与分析', module: 'score-tasks', permission: 'score:tasks' },
      { id: 'evaluation-tasks', title: '教学质量评价', path: '/next/edu/evaluation-tasks', icon: 'thumbs-up', blurb: '评教任务', module: 'evaluation-tasks', permission: 'evaluation:tasks' }
    ]
  },
  {
    id: 'wuyu',
    title: '成长评价',
    icon: 'star',
    permission: 'menu:wuyu',
    landing: 'activities',
    // 排序口径：期初配置 → 日常处理 → 激励发放 → 分析沉淀
    pages: [
      { id: 'indicators', title: '指标体系', path: '/next/wuyu/indicators', icon: 'target', blurb: '评价标准与生效起点', module: 'wuyu', permission: 'wuyu:indicators' },
      { id: 'plan-center', title: '活动管理', path: '/next/wuyu/plan-center', icon: 'folder', blurb: '建活动、管状态、看进度', module: 'wuyu', permission: 'wuyu:plan-center' },
      { id: 'activities', title: '成长活动', path: '/next/wuyu/activities', icon: 'activity', blurb: '执行工作台', module: 'wuyu', permission: 'wuyu:activities' },
      { id: 'instant', title: '即时点评', path: '/next/wuyu/instant', icon: 'star', blurb: '课堂高频点评', module: 'wuyu', permission: 'wuyu:instant' },
      { id: 'incentive', title: '成长激励', path: '/next/wuyu/incentive', icon: 'award', blurb: '勋章与积分计划', module: 'wuyu', permission: 'wuyu:incentive' },
      { id: 'mall', title: '商城管理', path: '/next/wuyu/mall', icon: 'gift', blurb: '兑换与余额', module: 'wuyu', permission: 'wuyu:mall' },
      { id: 'insight', title: '成长洞察', path: '/next/wuyu/insight', icon: 'trending-up', blurb: '班级与个体画像', module: 'wuyu', permission: 'wuyu:insight' },
      { id: 'reports', title: '报告中心', path: '/next/wuyu/reports', icon: 'file-text', blurb: '学期在线报告', module: 'wuyu', permission: 'wuyu:reports' },
      { id: 'resources', title: '资源中心', path: '/next/wuyu/resources', icon: 'layers', blurb: '官方案例与活动方案模板', module: 'wuyu', permission: 'wuyu:resources' }
    ]
  },
  {
    id: 'club',
    title: '社团管理',
    icon: 'flag',
    permission: 'menu:club',
    landing: 'overview',
    pages: [
      { id: 'overview', title: '社团总览', path: '/next/club/overview', icon: 'users', blurb: '社团档案与成员', module: 'club', permission: 'club:overview' },
      { id: 'seasons', title: '招新季管理', path: '/next/club/seasons', icon: 'calendar', blurb: '招新季与志愿热度', module: 'club', permission: 'club:seasons' },
      { id: 'review', title: '申请审核', path: '/next/club/review', icon: 'clipboard-check', blurb: '志愿录取与退社转社', module: 'club', permission: 'club:review' }
    ]
  },
  {
    id: 'data',
    title: '数据管理',
    icon: 'database',
    permission: 'menu:data',
    landing: 'students',
    pages: [
      { id: 'students', title: '学生管理', path: '/next/data/students', icon: 'users', blurb: '学籍与名册', module: 'data', permission: 'data:students' },
      { id: 'staff', title: '教职工管理', path: '/next/data/staff', icon: 'briefcase', blurb: '教职工档案', module: 'data', permission: 'data:staff' },
      { id: 'positions', title: '岗位管理', path: '/next/data/positions', icon: 'briefcase', blurb: '岗位定义与分配', module: 'data', permission: 'data:staff' },
      { id: 'subjects', title: '科目管理', path: '/next/data/subjects', icon: 'book', blurb: '学科科目设置', module: 'data', permission: 'data:subjects' }
    ]
  },
  {
    id: 'basic-config',
    title: '基础配置',
    icon: 'sliders',
    permission: 'menu:basic-config',
    landing: 'academic-years',
    pages: [
      { id: 'academic-years', title: '学年学期管理', path: '/next/data/academic-years', icon: 'clock', blurb: '学期起止与当前学期', module: 'basic-config', permission: 'data:academic-years' },
      { id: 'grade-class', title: '年级班级管理', path: '/next/settings/grade-class', icon: 'grid', blurb: '学段年级班级', module: 'basic-config', permission: 'settings:grade-class' },
      { id: 'building-room', title: '楼宇教室管理', path: '/next/settings/building-room', icon: 'map-pin', blurb: '场地与教室', module: 'basic-config', permission: 'settings:building-room' },
      { id: 'schedule-settings', title: '作息时间管理', path: '/next/settings/schedule', icon: 'bell', blurb: '节次与作息', module: 'basic-config', permission: 'settings:schedule' }
    ]
  },
  {
    id: 'settings',
    title: '系统设置',
    icon: 'settings',
    permission: 'menu:settings',
    landing: 'organization',
    pages: [
      { id: 'organization', title: '组织架构', path: '/next/settings/organization', icon: 'building', blurb: '校区与部门', module: 'settings', permission: 'settings:organization' },
      { id: 'roles', title: '角色权限', path: '/next/settings/roles', icon: 'shield', blurb: '角色与菜单勾选', module: 'settings', permission: 'settings:roles' },
      { id: 'passwords', title: '密码管理', path: '/next/settings/passwords', icon: 'lock', blurb: '账号密码策略', module: 'settings', permission: 'settings:password-management' },
      { id: 'assistant', title: '助手管理', path: '/next/settings/assistant', icon: 'bot', blurb: 'AI 助手配置', module: 'settings', permission: 'settings:assistant' }
    ]
  }
]

/**
 * 路径 → 所属订阅模块码。
 *
 * 用于路由守卫：菜单虽然按"已订阅模块"隐藏了，但直接输入 URL 仍能到达页面，
 * 因此守卫层必须按同一口径再拦一次，否则未订购模块的页面仍可被访问。
 * 不在任何模块下（工作台、帮助页等）返回 null，表示不受订阅限制。
 */
export function moduleOfPath(path: string): ModuleCode | null {
  for (const mod of NEXT_MODULES) {
    for (const page of mod.pages) {
      if (path === page.path || path.startsWith(page.path + '/')) {
        return page.module
      }
    }
  }
  return null
}

/**
 * 导航模型：模块 × 页面清单 × 默认落地页的唯一事实来源。
 * 当前角色从登录态（user store 的激活角色）派生，切换走 /auth/switch-role；
 * 页面可见性 = 租户已订阅 + 权限已勾选，与角色档位无关。
 */
export function useNextNav() {
  const userStore = useUserStore()

  /** 当前激活角色编码（切换器提交 /auth/switch-role 的目标码） */
  const currentRole = computed<string>(() => userStore.activeRoleCode ?? '')

  /** 当前激活角色展示名：后端角色名优先（如"德育管理员"），缺失时回退编码 */
  const currentRoleName = computed<string>(() => {
    const code = currentRole.value
    const owned = userStore.userInfo?.roles?.find((ref) => ref.code === code)
    return owned?.name?.trim() || code
  })

  /** 切换器数据源：用户实际拥有的角色（后端角色名优先；未带角色列表时以激活角色兜底） */
  const availableRoles = computed<{ code: string; name: string }[]>(() => {
    const roles = userStore.userInfo?.roles
    if (roles?.length) {
      return roles.map((ref) => ({ code: ref.code, name: ref.name?.trim() || ref.code }))
    }
    const code = currentRole.value
    return code ? [{ code, name: code }] : []
  })

  /**
   * 登录态携带的租户已订阅模块清单。
   * null = 登录态未提供订阅信息（如平台侧账号），视为不限制——与工作台卡片口径一致。
   */
  const purchasedModules = computed<string[] | null>(() => userStore.userInfo?.purchasedModules ?? null)

  /**
   * 登录态携带的角色权限码集合。
   * null = 登录态未提供权限列表（如平台侧账号），视为不限制；
   * 空集合 = 有权限列表但为空（该角色未勾任何页面）——逐页严格过滤。
   */
  const grantedPermissions = computed<Set<string> | null>(() => {
    const permissions = userStore.userInfo?.permissions
    return Array.isArray(permissions) ? new Set(permissions) : null
  })

  /** 模块下已订阅且已授权的页面（页面级裁剪的唯一入口） */
  function effectivePages(mod: NextModuleDef): NextPageDef[] {
    const purchased = purchasedModules.value
    const granted = grantedPermissions.value
    return mod.pages.filter((p) => {
      if (purchased !== null && !purchased.includes(p.module)) return false
      if (granted !== null && !granted.has(p.permission)) return false
      return true
    })
  }

  /** 可见模块：持有模块菜单码、且至少 1 页可见 */
  function modulesFor(): NextModuleDef[] {
    const granted = grantedPermissions.value
    return NEXT_MODULES.filter((m) => {
      if (granted !== null && !granted.has(m.permission)) return false
      return effectivePages(m).length > 0
    })
  }

  function pagesFor(moduleId: string): NextPageDef[] {
    const mod = NEXT_MODULES.find((m) => m.id === moduleId)
    return mod ? effectivePages(mod) : []
  }

  /** 全部可见模块的二级页面平铺清单（顺序 = 模块顺序 × 模块内页序；固定入口不在内） */
  function allVisiblePages(): NextPageDef[] {
    return modulesFor().flatMap((m) => effectivePages(m))
  }

  function landingPathOf(moduleId: string): string | null {
    const mod = NEXT_MODULES.find((m) => m.id === moduleId)
    if (!mod) return null
    const pages = effectivePages(mod)
    const landing = pages.find((p) => p.id === mod.landing) ?? pages[0]
    return landing?.path ?? null
  }

  /**
   * 不进左栏、但归属某个二级页面的子路由。
   * 例如成长任务的新建 / 编辑向导挂在方案中心下，否则直达 URL 会被送回工作台。
   */
  const ATTACHED_ROUTES: { prefix: string; parent: string }[] = [
    { prefix: '/next/wuyu/task', parent: '/next/wuyu/plan-center' },
    { prefix: '/next/wuyu/task2', parent: '/next/wuyu/plan-center' },
    { prefix: '/next/wuyu/toolbox', parent: '/next/wuyu/plan-center' }
  ]

  function resolvePath(path: string): string {
    const attached = ATTACHED_ROUTES.find((item) => path === item.prefix || path.startsWith(`${item.prefix}/`))
    return attached?.parent ?? path
  }

  /** 页面匹配：精确命中，或命中其子路由（如 /next/wuyu/incentive/plan/:id 归属激励中心）。
   *  只匹配"已授权且租户已订阅"的页面，直达 URL 越权同样被拦。 */
  function matchPage(path: string): { moduleId: string } | null {
    const resolved = resolvePath(path)
    for (const m of NEXT_MODULES) {
      for (const p of effectivePages(m)) {
        if (resolved === p.path || resolved.startsWith(`${p.path}/`)) return { moduleId: m.id }
      }
    }
    return null
  }

  function moduleIdByPath(path: string): string | null {
    return matchPage(path)?.moduleId ?? null
  }

  function isPathAllowed(path: string): boolean {
    if (ALWAYS_ALLOWED_PATHS.includes(path)) return true
    return matchPage(path) !== null
  }

  /** 导航高亮：精确页、其子路由，以及挂在该页下的向导（如成长任务新建） */
  function isPageActive(pagePath: string, path: string): boolean {
    if (path === pagePath || path.startsWith(`${pagePath}/`)) return true
    return resolvePath(path) === pagePath
  }

  return {
    currentRole,
    currentRoleName,
    availableRoles,
    modulesFor,
    pagesFor,
    allVisiblePages,
    landingPathOf,
    moduleIdByPath,
    isPathAllowed,
    isPageActive
  }
}
